import * as store from '@attain-apps/fe/web/main/lib/store'
import { TypeChecker } from '@attain-apps/type-guards'

import type { Collection } from '../collection/collection'
import type * as models from '../models'

/**
 * Represents an abstract plugin.
 */
export abstract class Plugin<
  TTypeParams extends models.TypeParams,
  TId extends Plugin.Id,
  TState,
  TSelectors,
  TActionsConfig extends store.actions.Config.Constraint,
> {
  /** The entity collection associated with the plugin. */
  public readonly collection: Collection<TTypeParams>
  /** The ID of the plugin. */
  public readonly id: TId
  /**
   * {@link store.Action.Group} for this {@link Plugin}
   */
  // NOTE: intentionally not typed here so that type can be inferred
  public readonly feature

  constructor({
    collection,
    id,
    actions,
    initialState,
    selectors,
  }: Readonly<
    Plugin.constructor.Params<
      TTypeParams,
      TId,
      TState,
      TSelectors,
      TActionsConfig
    >
  >) {
    this.collection = collection
    this.id = id
    this.feature = new store.Feature({
      actions,
      // NOTE: effects are started by the manager once all plugins have been
      // instantiated
      autoInitializeEffects: false,
      initialState,
      name: id,
      parent: this.collection.plugins.feature,
      selectors,
    })
  }
}
export namespace Plugin {
  export type Constraint<
    TTypeParams extends models.TypeParams = any,
    TId extends Plugin.Id = any,
    TState = any,
    TSelectors = any,
    TActionsConfig extends store.actions.Config.Constraint = any,
  > = Plugin<TTypeParams, TId, TState, TSelectors, TActionsConfig>
  export type Default<
    TTypeParams extends models.TypeParams = models.TypeParams,
    TId extends Plugin.Id = Plugin.Id,
    TState = unknown,
    TSelectors = unknown,
    TActionsConfig extends
      store.actions.Config.Constraint = store.actions.Config.Default,
  > = Plugin<TTypeParams, TId, TState, TSelectors, TActionsConfig>

  export namespace constructor {
    export interface Params<
      TTypeParams extends models.TypeParams,
      TId extends Id,
      TState,
      TSelectors,
      TActionsConfig extends store.actions.Config.Constraint,
    > extends Pick<
        store.Feature.constructor.Params<TState, TSelectors, TActionsConfig>,
        'initialState' | 'selectors' | 'actions'
      > {
      collection: Collection<TTypeParams>
      id: TId
    }
  }

  export type Id = Id.Standard | Id.Custom
  export namespace Id {
    export const isId: TypeChecker<Id> = TypeChecker.Factory.lazy(() =>
      TypeChecker.Factory.union(Id.Standard.isStandard, Id.Custom.isCustom),
    )
    export enum Standard {
      ApiExecution = 'ApiExecution',
      Attribute = 'Attribute',
      Cache = 'Cache',
      CurrentEntity = 'CurrentEntity',
      CurrentList = 'CurrentList',
      DataOperation = 'DataOperation',
      Editing = 'Editing',
      Group = 'Group',
      Selection = 'Selection',
    }
    const STANDARD: ReadonlySet<Standard> = new Set(
      Object.values(Standard) as Standard[],
    )
    export namespace Standard {
      export const ALL: readonly Standard[] = Array.from(STANDARD)
      export const isStandard: TypeChecker<Standard> =
        TypeChecker.Factory.oneOf(STANDARD)
    }

    export type Custom<TName extends string = string> = `?${TName}`
    export namespace Custom {
      export const isCustom: TypeChecker<Custom> = TypeChecker.Factory.custom(
        (source: any): source is Custom =>
          TypeChecker.isString(source) && source.startsWith('?'),
        { typeDescription: 'ec.Plugin.Id.Custom' },
      )

      export function create<TName extends string>(name: TName): Custom<TName> {
        return `?${name}`
      }
    }
  }

  /**
   * TODO:
   */
  export interface Factory<
    TTypeParams extends models.TypeParams,
    TId extends Id,
    TPlugin extends Plugin.Constraint<TTypeParams, TId>,
  > {
    readonly id: TId
    /**
     * TODO: add documentation
     *
     * Can return `undefined` to not create a {@link Plugin} for this
     * {@link Collection} (e.g. if it is not necessary based on the specific
     * configuration of the {@link Collection})
     */
    produceFor(collection: Collection<TTypeParams>): TPlugin | undefined
  }
}
